import { useId } from "react";
import { Globe2, MapPin, Sun } from "lucide-react";
import { PH_REGIONS, type Market, type PhRegion } from "@shared/constants/market";
import { IconChip } from "../ui/IconChip";
import { cn } from "../../lib/cn";

export interface RegionPickerValue {
  market: Market | null;
  phRegion: PhRegion | null;
}

export interface RegionPickerProps {
  value: RegionPickerValue;
  onChange: (next: { market: Market; phRegion: PhRegion | null }) => void;
  /** Shown under the city list (e.g. "Choose your city" from accountRegionSchema). */
  cityError?: string;
  disabled?: boolean;
  /** The visible question; the onboarding sheet can pass its own wording. */
  legend?: string;
}

const MARKET_OPTIONS: { id: Market; label: string; hint: string; icon: typeof Sun }[] = [
  {
    id: "ph",
    label: "Philippines",
    hint: "Leena's List Philippines: gentle season cards for your city, with local hotlines.",
    icon: Sun,
  },
  {
    id: "main",
    label: "United States & elsewhere",
    hint: "The main version of Leena's List.",
    icon: Globe2,
  },
];

/**
 * "Where is your family based?" — the market choice, plus the city for the
 * Philippines. Controlled and form-agnostic so the Region settings page and
 * (later) the onboarding welcome sheet can share it. Native radio inputs, so
 * keyboard and screen readers get standard radio-group behavior.
 *
 * The chosen city is kept while the parent flips between markets, so changing
 * their mind costs nothing; accountRegionSchema drops it for the main market
 * when the choice is saved.
 */
export function RegionPicker({
  value,
  onChange,
  cityError,
  disabled = false,
  legend = "Where is your family based?",
}: RegionPickerProps) {
  const name = useId();
  const cityName = useId();
  const cityErrorId = useId();

  return (
    <div className="space-y-5">
      <fieldset className="space-y-2" disabled={disabled}>
        <legend className="font-display text-base font-semibold text-neutral-900">{legend}</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {MARKET_OPTIONS.map(({ id, label, hint, icon }) => {
            const checked = value.market === id;
            return (
              <label
                key={id}
                className={cn(
                  // A glass choice; the chosen one gets a sky ring and wash.
                  "flex cursor-pointer items-start gap-3 rounded-2xl bg-white/85 p-4 ring-1 transition hover:ring-sky-300",
                  checked ? "bg-sky-50/90 shadow-soft ring-2 ring-sky-400" : "ring-sky-100",
                )}
              >
                <input
                  type="radio"
                  name={name}
                  value={id}
                  checked={checked}
                  onChange={() => onChange({ market: id, phRegion: value.phRegion })}
                  className="mt-1 h-4 w-4 shrink-0 accent-sky-600"
                />
                <IconChip icon={icon} accent={id === "ph" ? "amber" : "sky"} size="sm" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-neutral-900">{label}</span>
                  <span className="mt-0.5 block text-xs text-neutral-600">{hint}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {value.market === "ph" && (
        <fieldset
          className="space-y-2"
          disabled={disabled}
          aria-describedby={cityError ? cityErrorId : undefined}
        >
          <legend className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900">
            <MapPin className="h-4 w-4 text-amber-600" aria-hidden />
            Which city is closest?
          </legend>
          <p className="text-xs text-neutral-600">
            Season cards follow your city: typhoon prep for most, flood prep where storms rarely
            hit directly.
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {PH_REGIONS.map((city) => {
              const checked = value.phRegion === city;
              return (
                <label
                  key={city}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-full bg-white/85 px-3.5 py-2.5 text-sm ring-1 transition hover:ring-amber-300",
                    checked ? "bg-amber-50/90 font-semibold text-neutral-900 ring-2 ring-amber-400" : "text-neutral-800 ring-amber-100",
                  )}
                >
                  <input
                    type="radio"
                    name={cityName}
                    value={city}
                    checked={checked}
                    onChange={() => onChange({ market: "ph", phRegion: city })}
                    className="h-4 w-4 shrink-0 accent-amber-600"
                  />
                  {city}
                </label>
              );
            })}
          </div>
          {cityError && (
            <p id={cityErrorId} role="alert" className="text-sm text-red-700">
              {cityError}
            </p>
          )}
        </fieldset>
      )}
    </div>
  );
}
